import React, { useState, useEffect } from 'react';
import { getLocationById } from './config/locationConfig';
import { useAuth } from '../../../hooks/AuthProvider';
import { getFirestore, getDoc, doc } from 'firebase/firestore';
import app from '../../../Firebase';

interface Store {
  id?: string;
  name: string;
  description: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  email: string;
  website?: string;
  timezone: string;
  currency: string;
  // Test environment Stripe details
  stripeTestAccountId?: string;
  stripeTestAccountName?: string;
  stripeTestConnected: boolean;
  stripeTestConnectUrl?: string;
  stripeTestConnectUrlExpires?: Date | any;
  stripeTestOnboardingStarted?: Date | any;
  // Live environment Stripe details
  stripeLiveAccountId?: string;
  stripeLiveAccountName?: string;
  stripeLiveConnected: boolean;
  stripeLiveConnectUrl?: string;
  stripeLiveConnectUrlExpires?: Date | any;
  stripeLiveOnboardingStarted?: Date | any;
  // Legacy fields for backward compatibility
  stripeAccountId?: string;
  stripeAccountName?: string;
  stripeConnected: boolean;
  stripeConnectUrl?: string;
  stripeConnectUrlExpires?: Date | any;
  stripeOnboardingStarted?: Date | any;
  isActive: boolean;
  createdAt: Date | any;
  updatedAt: Date | any;
}

interface AddStoreProps {
  onSubmit: (storeData: Store) => void;
  onCancel: () => void;
  appRegion: string;
  locationId: string;
}

const AddStore: React.FC<AddStoreProps> = ({
  onSubmit,
  onCancel,
  appRegion,
  locationId
}) => {
  const { currentUser } = useAuth();
  const [isAppAdmin, setIsAppAdmin] = useState<boolean>(false);
  const [checkingPermission, setCheckingPermission] = useState<boolean>(true);
  const [formData, setFormData] = useState<Store>({
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    timezone: '',
    currency: '',
    // Initialize test environment Stripe fields
    stripeTestConnected: false,
    stripeTestAccountId: undefined,
    stripeTestAccountName: undefined,
    stripeTestConnectUrl: undefined,
    stripeTestConnectUrlExpires: undefined,
    stripeTestOnboardingStarted: undefined,
    // Initialize live environment Stripe fields
    stripeLiveConnected: false,
    stripeLiveAccountId: undefined,
    stripeLiveAccountName: undefined,
    stripeLiveConnectUrl: undefined,
    stripeLiveConnectUrlExpires: undefined,
    stripeLiveOnboardingStarted: undefined,
    // Legacy fields for backward compatibility
    stripeConnected: false,
    stripeAccountId: undefined,
    stripeAccountName: undefined,
    stripeConnectUrl: undefined,
    stripeConnectUrlExpires: undefined,
    stripeOnboardingStarted: undefined,
    isActive: false,
    createdAt: new Date(),
    updatedAt: new Date()
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generatedStoreId, setGeneratedStoreId] = useState<string>('');
  const [customStoreId, setCustomStoreId] = useState<string>('');
  const [isEditingStoreId, setIsEditingStoreId] = useState<boolean>(false);

  // Get location configuration
  const locationConfig = getLocationById(locationId);

  // Check if user has APP_ADMIN permission
  useEffect(() => {
    const checkAdminPermission = async () => {
      if (!currentUser || !currentUser.email) {
        setIsAppAdmin(false);
        setCheckingPermission(false);
        return;
      }
      
      try {
        const db = getFirestore(app);
        const userDocRef = doc(db, 'users', currentUser.email);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userPermissions = userData?.permissions || [];
          setIsAppAdmin(userPermissions.includes('APP_ADMIN'));
        } else {
          setIsAppAdmin(false);
        }
      } catch (error) {
        console.error('Error checking admin permission:', error);
        setIsAppAdmin(false);
      } finally {
        setCheckingPermission(false);
      }
    };
    
    if (currentUser) {
      checkAdminPermission();
    } else {
      setIsAppAdmin(false);
      setCheckingPermission(false);
    }
  }, [currentUser]);

  // Generate store ID from name
  const generateStoreId = (name: string): string => {
    if (!name.trim()) return '';
    
    // Convert to lowercase, replace spaces with hyphens, remove special chars
    const baseId = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    // Generate 6-digit hexadecimal
    const hex = Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
    
    return `${baseId}-${hex}`;
  };

  // Update store ID when name changes (only if not editing custom ID)
  useEffect(() => {
    if (!isEditingStoreId) {
      const newId = generateStoreId(formData.name);
      setGeneratedStoreId(newId);
      setCustomStoreId(newId);
    }
  }, [formData.name, isEditingStoreId]);

  // Set default values based on location
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      currency: locationConfig.currencyCode,
      country: locationConfig.country,
      timezone: locationConfig.availableTimezones[0]
    }));
  }, [locationConfig]);

  const handleInputChange = (field: keyof Store, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleStoreIdChange = (value: string) => {
    setCustomStoreId(value);
    setIsEditingStoreId(true);
    
    // Clear store ID error when user starts typing
    if (errors.storeId) {
      setErrors(prev => ({
        ...prev,
        storeId: ''
      }));
    }
  };

  const handleRegenerateStoreId = () => {
    const newId = generateStoreId(formData.name);
    setGeneratedStoreId(newId);
    setCustomStoreId(newId);
    setIsEditingStoreId(false);
    
    // Clear any store ID errors
    if (errors.storeId) {
      setErrors(prev => ({
        ...prev,
        storeId: ''
      }));
    }
  };

  const validateStoreId = (storeId: string): boolean => {
    if (!storeId.trim()) {
      return false;
    }
    
    // Basic validation: should contain only lowercase letters, numbers, and hyphens
    // Should not start or end with hyphen
    const storeIdRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
    return storeIdRegex.test(storeId);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) newErrors.name = 'Store name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State/Province is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'Zip/Postal code is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';

    // Store ID validation
    const finalStoreId = customStoreId || generatedStoreId;
    if (!finalStoreId.trim()) {
      newErrors.storeId = 'Store ID is required';
    } else if (!validateStoreId(finalStoreId)) {
      newErrors.storeId = 'Store ID must contain only lowercase letters, numbers, and hyphens. Cannot start or end with hyphen.';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (basic)
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (formData.phone && !phoneRegex.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Website validation (if provided)
    if (formData.website && formData.website.trim()) {
      const urlRegex = /^https?:\/\/.+/;
      if (!urlRegex.test(formData.website)) {
        newErrors.website = 'Website must start with http:// or https://';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Use custom store ID if set, otherwise use generated one
      const finalStoreId = customStoreId || generatedStoreId;
      
      const storeWithId = {
        ...formData,
        id: finalStoreId,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      onSubmit(storeWithId);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container add-store-modal">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Add New Store</h3>
            <button 
              type="button" 
              className="modal-close"
              onClick={onCancel}
            >
              ×
            </button>
          </div>
          
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                {/* Basic Information */}
                <div className="form-section">
                  <h4>Basic Information</h4>
                  
                  <div className="form-field">
                    <label>Store Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter store name"
                      className={errors.name ? 'error' : ''}
                    />
                    {errors.name && <span className="error-text">{errors.name}</span>}
                  </div>

                  {(generatedStoreId || customStoreId) && (
                    <div className="form-field">
                      <label>
                        Store ID {isAppAdmin ? '*' : '(Auto-generated)'}
                        {isAppAdmin && (
                          <button
                            type="button"
                            onClick={handleRegenerateStoreId}
                            className="regenerate-btn"
                            style={{
                              marginLeft: '10px',
                              padding: '2px 8px',
                              fontSize: '12px',
                              backgroundColor: '#f0f0f0',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Regenerate
                          </button>
                        )}
                      </label>
                      <input
                        type="text"
                        value={customStoreId}
                        onChange={(e) => isAppAdmin ? handleStoreIdChange(e.target.value) : undefined}
                        readOnly={!isAppAdmin}
                        className={`${errors.storeId ? 'error' : ''} ${!isAppAdmin ? 'readonly-input' : ''}`}
                        style={!isAppAdmin ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                        placeholder={isAppAdmin ? "Enter store ID" : ""}
                      />
                      {errors.storeId && <span className="error-text">{errors.storeId}</span>}
                      <small className="form-help-text">
                        {isAppAdmin 
                          ? "This ID will be used to identify your store in the system. Use lowercase letters, numbers, and hyphens only." 
                          : "This ID will be used to identify your store in the system."
                        }
                      </small>
                    </div>
                  )}

                  <div className="form-field">
                    <label>Description *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe your store"
                      rows={3}
                      className={errors.description ? 'error' : ''}
                    />
                    {errors.description && <span className="error-text">{errors.description}</span>}
                  </div>
                </div>

                {/* Address Information */}
                <div className="form-section">
                  <h4>Address Information</h4>
                  
                  <div className="form-field">
                    <label>Street Address *</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder="Enter street address"
                      className={errors.address ? 'error' : ''}
                    />
                    {errors.address && <span className="error-text">{errors.address}</span>}
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label>City *</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="City"
                        className={errors.city ? 'error' : ''}
                      />
                      {errors.city && <span className="error-text">{errors.city}</span>}
                    </div>

                    <div className="form-field">
                      <label>State/Province *</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        placeholder="State or Province"
                        className={errors.state ? 'error' : ''}
                      />
                      {errors.state && <span className="error-text">{errors.state}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label>Zip/Postal Code *</label>
                      <input
                        type="text"
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange('zipCode', e.target.value)}
                        placeholder="Zip or postal code"
                        className={errors.zipCode ? 'error' : ''}
                      />
                      {errors.zipCode && <span className="error-text">{errors.zipCode}</span>}
                    </div>

                    <div className="form-field">
                      <label>Country *</label>
                      <select
                        value={formData.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                      >
                        {locationConfig.availableCountries.map(country => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="form-section">
                  <h4>Contact Information</h4>
                  
                  <div className="form-field">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="Phone number"
                      className={errors.phone ? 'error' : ''}
                    />
                    {errors.phone && <span className="error-text">{errors.phone}</span>}
                  </div>

                  <div className="form-field">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Email address"
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>

                  <div className="form-field">
                    <label>Website (Optional)</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://www.example.com"
                      className={errors.website ? 'error' : ''}
                    />
                    {errors.website && <span className="error-text">{errors.website}</span>}
                  </div>
                </div>

                {/* Business Settings */}
                <div className="form-section">
                  <h4>Business Settings</h4>
                  
                  <div className="form-row">
                    <div className="form-field">
                      <label>Timezone *</label>
                      <select
                        value={formData.timezone}
                        onChange={(e) => handleInputChange('timezone', e.target.value)}
                      >
                        {locationConfig.availableTimezones.map(timezone => (
                          <option key={timezone} value={timezone}>
                            {timezone}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-field">
                      <label>Currency *</label>
                      <select
                        value={formData.currency}
                        onChange={(e) => handleInputChange('currency', e.target.value)}
                      >
                        {locationConfig.availableCurrencies.map(currency => (
                          <option key={currency} value={currency}>
                            {currency}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Location Info Display */}
                <div className="form-section">
                  <h4>Organization & Location</h4>
                  <div className="location-display">
                    <div className="location-info-item">
                      <label>Organization:</label>
                      <span>{locationConfig.organizationName}</span>
                    </div>
                    <div className="location-info-item">
                      <label>Location:</label>
                      <span>{locationConfig.locationName}, {locationConfig.country}</span>
                    </div>
                    <div className="location-info-item">
                      <label>Region:</label>
                      <span>{locationConfig.appRegion}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stripe-connect-info">
                <div className="info-box">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                  <div>
                    <p><strong>Next Step: Stripe Connect</strong></p>
                    <p>After creating this store, you'll need to connect Stripe accounts ({locationConfig.organizationName}) for both test and live environments to process payments. You can use existing accounts or connect new ones.</p>
                  </div>
                </div>
              </div>
            </form>
          </div>
          
          <div className="modal-footer">
            <button
              type="button"
              className="submit-btn"
              onClick={handleSubmit}
            >
              Create Store
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStore;